import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
// register + auto-login → user lands on visitor home with a real session
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff, ArrowLeft, Check, Loader2 } from 'lucide-react';

const MONTHS = [
  { value: '1', label: 'Janvier' },
  { value: '2', label: 'Février' },
  { value: '3', label: 'Mars' },
  { value: '4', label: 'Avril' },
  { value: '5', label: 'Mai' },
  { value: '6', label: 'Juin' },
  { value: '7', label: 'Juillet' },
  { value: '8', label: 'Août' },
  { value: '9', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 1920 + 1 }, (_, i) => currentYear - i);
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

const Register = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    dobDay: '',
    dobMonth: '',
    dobYear: '',
    rgpdConsent: false,
  });
  const [errors, setErrors] = useState({});
  const [debugInfo, setDebugInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Refs for scroll-to-first-error
  const fieldRefs = {
    firstName: useRef(null),
    lastName: useRef(null),
    gender: useRef(null),
    email: useRef(null),
    password: useRef(null),
    confirmPassword: useRef(null),
    postalCode: useRef(null),
    country: useRef(null),
    rgpdConsent: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const passwordRequirements = [
    { label: 'Au moins 8 caractères', met: formData.password.length >= 8 },
    { label: 'Les mots de passe correspondent', met: formData.password && formData.password === formData.confirmPassword },
  ];

  // Order matters — used for scroll-to-first-error
  const FIELD_ORDER = ['firstName', 'lastName', 'gender', 'email', 'password', 'confirmPassword', 'postalCode', 'country', 'rgpdConsent'];

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'Ce champ est obligatoire';
    if (!formData.lastName.trim()) newErrors.lastName = 'Ce champ est obligatoire';
    if (!formData.gender) newErrors.gender = 'Ce champ est obligatoire';

    if (!formData.email.trim()) {
      newErrors.email = 'Ce champ est obligatoire';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (!formData.password) {
      newErrors.password = 'Ce champ est obligatoire';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Ce champ est obligatoire';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Ce champ est obligatoire';
    } else if (!/^\d{5}$/.test(formData.postalCode)) {
      newErrors.postalCode = 'Le code postal doit contenir 5 chiffres';
    }

    if (!formData.country.trim()) newErrors.country = 'Ce champ est obligatoire';

    if (!formData.rgpdConsent) {
      newErrors.rgpdConsent = 'Vous devez accepter la politique de confidentialité';
    }

    setErrors(newErrors);

    // Smooth scroll to first error
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = FIELD_ORDER.find(f => newErrors[f]);
      if (firstErrorField && fieldRefs[firstErrorField]?.current) {
        fieldRefs[firstErrorField].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        try { fieldRefs[firstErrorField].current.focus(); } catch (_) {}
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setDebugInfo(null);

    const result = await register({
      firstName: formData.firstName,
      lastName: formData.lastName,
      gender: formData.gender,
      email: formData.email,
      password: formData.password,
      postalCode: formData.postalCode,
      country: formData.country,
      rgpdConsent: formData.rgpdConsent,
      phone: formData.phone || undefined,
      dateOfBirth: (formData.dobYear && formData.dobMonth && formData.dobDay)
        ? `${formData.dobYear}-${formData.dobMonth.padStart(2, '0')}-${formData.dobDay.padStart(2, '0')}`
        : undefined,
      address: formData.address || undefined,
      city: formData.city || undefined,
    });

    if (result.success) {
      // Auto-login so the user lands on the visitor home with a real session.
      // No waiting state, no banner — they just get access immediately.
      const loginResult = await login({ email: formData.email, password: formData.password });
      if (loginResult.success) {
        toast({ title: 'Bienvenue !', description: 'Votre compte a été créé.' });
        navigate('/');
      } else {
        // Edge case: registration worked but auto-login failed → fall back to login page
        toast({ title: 'Inscription réussie', description: 'Veuillez vous connecter.' });
        navigate('/login');
      }
      setLoading(false);
      return;
    } else {
      // Merge backend field errors with current client errors
      const backendFieldErrors = result.fieldErrors || {};
      if (Object.keys(backendFieldErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...backendFieldErrors }));
        // Scroll to the first field that has a backend error
        const firstBackendField = FIELD_ORDER.find(f => backendFieldErrors[f]);
        if (firstBackendField && fieldRefs[firstBackendField]?.current) {
          fieldRefs[firstBackendField].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          try { fieldRefs[firstBackendField].current.focus(); } catch (_) {}
        }
      }
      // Keep a debug line for the user (helps us/them diagnose)
      setDebugInfo({ status: result.status, error: result.error, debug: result.debug });
      toast({
        title: 'Erreur',
        description: result.error,
        variant: 'destructive',
      });
    }

    setLoading(false);
  };

  // Shared styling
  const inputClass = `h-11 rounded-xl ${'bg-pcc-surface2 border-pcc-card-border text-pcc-text'}`;
  const inputErrorClass = (field) => errors[field] ? 'border-red-500' : '';
  const selectClass = `h-11 rounded-xl w-full px-3 text-sm border outline-none focus:ring-2 focus:ring-[#9398DE] ${'bg-pcc-surface2 border-pcc-card-border text-pcc-text'}`;
  const sectionTitleClass = `text-xs font-semibold uppercase tracking-wider mb-3 ${'text-pcc-text-muted'}`;
  const dividerClass = `border-t ${'border-pcc-card-border'} pt-6 mt-6`;
  const labelClass = `block text-sm font-medium ${'text-pcc-text-secondary'}`;
  const optionalClass = `text-xs font-normal ${'text-pcc-text-muted'}`;

  return (
    <div className={`min-h-screen flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-200 ${'bg-pcc-bg'}`}>
      <Card className={`w-full max-w-md border-0 shadow-xl ${'bg-pcc-card border border-pcc-card-border'}`}>
        <CardHeader className="space-y-1">
          {/* Back button */}
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`absolute top-[max(env(safe-area-inset-top),12px)] left-4 p-2 transition-colors ${'text-pcc-text-muted hover:text-pcc-text'}`}
          >
            <ArrowLeft size={20} />
          </button>

          {/* Logo */}
          <div className="flex flex-col items-center justify-center mb-4">
            <img src="/logo-lion-light.png" alt="Paris Centre Chrétien" className="w-20 h-20 mb-2 hidden dark:block" />
            <img src="/logo-lion-dark.png" alt="Paris Centre Chrétien" className="w-20 h-20 mb-2 dark:hidden" />
            <span className={`text-sm font-medium tracking-wide ${'text-pcc-text-muted'}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Paris Centre Chrétien
            </span>
          </div>
          <CardTitle className={`text-2xl font-bold text-center ${'text-pcc-text'}`}>
            Créer un compte
          </CardTitle>
          <CardDescription className={`text-center ${'text-pcc-text-muted'}`}>
            Créez votre compte visiteur
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-1" data-testid="register-form" noValidate>
            {/* ───────── SECTION 1: IDENTITÉ ───────── */}
            <section>
              <h3 className={sectionTitleClass}>Identité</h3>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="space-y-1.5">
                  <Label htmlFor="firstName" className={labelClass}>Prénom *</Label>
                  <Input
                    ref={fieldRefs.firstName}
                    id="firstName"
                    name="firstName"
                    type="text"
                    placeholder="Jean"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`${inputClass} ${inputErrorClass('firstName')}`}
                    data-testid="register-firstname"
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="lastName" className={labelClass}>Nom *</Label>
                  <Input
                    ref={fieldRefs.lastName}
                    id="lastName"
                    name="lastName"
                    type="text"
                    placeholder="Dupont"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`${inputClass} ${inputErrorClass('lastName')}`}
                    data-testid="register-lastname"
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gender" className={labelClass}>Genre *</Label>
                <select
                  ref={fieldRefs.gender}
                  id="gender"
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className={`${selectClass} ${errors.gender ? 'border-red-500' : ''}`}
                  data-testid="register-gender"
                >
                  <option value="" disabled>Sélectionner</option>
                  <option value="Homme">Homme</option>
                  <option value="Femme">Femme</option>
                </select>
                {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
              </div>
            </section>

            {/* ───────── SECTION 2: CONNEXION ───────── */}
            <section className={dividerClass}>
              <h3 className={sectionTitleClass}>Connexion</h3>

              <div className="space-y-1.5 mb-4">
                <Label htmlFor="email" className={labelClass}>Email *</Label>
                <Input
                  ref={fieldRefs.email}
                  id="email"
                  name="email"
                  type="email"
                  placeholder="jean.dupont@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={`${inputClass} ${inputErrorClass('email')}`}
                  data-testid="register-email"
                />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div className="space-y-1.5 mb-4">
                <Label htmlFor="password" className={labelClass}>Mot de passe *</Label>
                <div className="relative">
                  <Input
                    ref={fieldRefs.password}
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleChange}
                    className={`${inputClass} pr-12 ${inputErrorClass('password')}`}
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${'text-pcc-text-muted hover:text-pcc-text'}`}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <div className="space-y-1.5 mb-3">
                <Label htmlFor="confirmPassword" className={labelClass}>Confirmer le mot de passe *</Label>
                <div className="relative">
                  <Input
                    ref={fieldRefs.confirmPassword}
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`${inputClass} pr-12 ${inputErrorClass('confirmPassword')}`}
                    data-testid="register-confirm-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 ${'text-pcc-text-muted hover:text-pcc-text'}`}
                    tabIndex={-1}
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              {/* Password requirements checklist */}
              <div className="space-y-1">
                {passwordRequirements.map((req, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.met ? 'bg-green-500' : 'bg-pcc-surface2'}`}>
                      {req.met && <Check size={10} className="text-white" />}
                    </div>
                    <span className={req.met ? ('text-green-500') : ('text-pcc-text-muted')}>
                      {req.label}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* ───────── SECTION 3: COORDONNÉES ───────── */}
            <section className={dividerClass}>
              <h3 className={sectionTitleClass}>Coordonnées</h3>

              <div className="space-y-1.5 mb-4">
                <Label htmlFor="phone" className={labelClass}>
                  Téléphone <span className={optionalClass}>(optionnel)</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+33 6 12 34 56 78"
                  value={formData.phone}
                  onChange={handleChange}
                  className={inputClass}
                  data-testid="register-phone"
                />
              </div>

              <div className="space-y-1.5 mb-4">
                <Label htmlFor="address" className={labelClass}>
                  Adresse <span className={optionalClass}>(optionnel)</span>
                </Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  placeholder="123 rue de la Paix"
                  value={formData.address}
                  onChange={handleChange}
                  className={inputClass}
                  data-testid="register-address"
                />
              </div>

              <div className="grid grid-cols-2 gap-3 mb-1">
                <div className="space-y-1.5">
                  <Label htmlFor="city" className={labelClass}>
                    Ville <span className={optionalClass}>(optionnel)</span>
                  </Label>
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder="Paris"
                    value={formData.city}
                    onChange={handleChange}
                    className={inputClass}
                    data-testid="register-city"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="postalCode" className={labelClass}>Code postal *</Label>
                  <Input
                    ref={fieldRefs.postalCode}
                    id="postalCode"
                    name="postalCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                    placeholder="75001"
                    value={formData.postalCode}
                    onChange={handleChange}
                    className={`${inputClass} ${inputErrorClass('postalCode')}`}
                    data-testid="register-postalcode"
                  />
                  {errors.postalCode && <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>}
                </div>
              </div>

              <div className="space-y-1.5 mt-4">
                <Label htmlFor="country" className={labelClass}>Pays *</Label>
                <Input
                  ref={fieldRefs.country}
                  id="country"
                  name="country"
                  type="text"
                  placeholder="France"
                  value={formData.country}
                  onChange={handleChange}
                  className={`${inputClass} ${inputErrorClass('country')}`}
                  data-testid="register-country"
                />
                {errors.country && <p className="text-red-500 text-xs mt-1">{errors.country}</p>}
              </div>
            </section>

            {/* ───────── SECTION 4: INFORMATIONS PERSONNELLES ───────── */}
            <section className={dividerClass}>
              <h3 className={sectionTitleClass}>Informations personnelles</h3>

              <div className="space-y-1.5">
                <Label className={labelClass}>
                  Date de naissance <span className={optionalClass}>(optionnel)</span>
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    name="dobDay"
                    value={formData.dobDay}
                    onChange={handleChange}
                    className={selectClass}
                    data-testid="register-dob-day"
                  >
                    <option value="">Jour</option>
                    {DAYS.map(d => <option key={d} value={String(d)}>{d}</option>)}
                  </select>
                  <select
                    name="dobMonth"
                    value={formData.dobMonth}
                    onChange={handleChange}
                    className={selectClass}
                    data-testid="register-dob-month"
                  >
                    <option value="">Mois</option>
                    {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <select
                    name="dobYear"
                    value={formData.dobYear}
                    onChange={handleChange}
                    className={selectClass}
                    data-testid="register-dob-year"
                  >
                    <option value="">Année</option>
                    {YEARS.map(y => <option key={y} value={String(y)}>{y}</option>)}
                  </select>
                </div>
              </div>
            </section>

            {/* ───────── SECTION 5: CONSENTEMENT ───────── */}
            <section className={dividerClass}>
              <h3 className={sectionTitleClass}>Consentement</h3>

              <div ref={fieldRefs.rgpdConsent} className="space-y-1.5">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="rgpdConsent"
                    name="rgpdConsent"
                    checked={formData.rgpdConsent}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded accent-[#9398DE] cursor-pointer flex-shrink-0"
                    data-testid="register-rgpd"
                  />
                  <label
                    htmlFor="rgpdConsent"
                    className={`text-sm leading-relaxed cursor-pointer ${'text-pcc-text-secondary'}`}
                  >
                    J'accepte que mes données soient utilisées par Paris Centre Chrétien conformément à sa politique de confidentialité <span className="text-red-500">*</span>
                  </label>
                </div>
                {errors.rgpdConsent && <p className="text-red-500 text-xs mt-1">{errors.rgpdConsent}</p>}
              </div>
            </section>

            {/* ───────── DEBUG INFO (shown only after a failed submission) ───────── */}
            {debugInfo && (
              <div
                className={`mt-6 rounded-xl p-3 text-xs ${'bg-pcc-red-soft text-pcc-ink-red'}`}
                data-testid="register-debug"
              >
                <p className="font-semibold mb-1">
                  Détails techniques {debugInfo.status ? `(HTTP ${debugInfo.status})` : ''}
                </p>
                <p className="leading-relaxed break-words">{debugInfo.error}</p>
                {debugInfo.debug && (
                  <details className="mt-1 opacity-80">
                    <summary className="cursor-pointer">Plus d'info</summary>
                    <pre className="whitespace-pre-wrap break-all mt-1 text-[11px]">{debugInfo.debug}</pre>
                  </details>
                )}
              </div>
            )}

            {/* ───────── SUBMIT ───────── */}
            <div className="pt-6">
              <Button
                type="submit"
                className="w-full h-12 bg-[#9398DE] hover:bg-[#7B80C9] text-white rounded-xl font-semibold"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="mr-2 animate-spin" />
                    Inscription...
                  </>
                ) : 'Créer mon compte'}
              </Button>
            </div>
          </form>

          <div className="mt-6 text-center text-sm">
            <span className={'text-pcc-text-muted'}>Déjà un compte? </span>
            <Link to="/login" className="text-[#9398DE] hover:underline font-semibold">
              Se connecter
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
